import React from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { App } from "./App";
import { HelmetProvider } from "react-helmet-async";

// Reload auto si Vite échoue à précharger un chunk : cas typique d'un deploy
// qui a remplacé les hashes _assets/* alors que le HTML servi avant le deploy
// référence encore l'ancien index.js. Le `import()` rejette avec "Unable to
// preload CSS for ..." ou "Failed to fetch dynamically imported module", la
// page reste blanche, l'utilisateur abandonne. Le reload récupère le HTML à
// jour avec les nouveaux hashes. Guard sessionStorage anti-boucle (max 1
// reload par fenêtre de 10s pour éviter une rafale si l'erreur persiste).
let lastPreloadReloadAt = 0;
window.addEventListener("vite:preloadError", (event) => {
  const RELOAD_KEY = "kenometric_preload_reload_at";
  const now = Date.now();
  // Guard anti-boucle. sessionStorage peut jeter un SecurityError si le stockage
  // est bloqué (Safari « bloquer tous les cookies », webview) → fallback sur un
  // flag module-level pour ne pas faire échouer le handler lui-même.
  let last = lastPreloadReloadAt;
  try {
    const stored = window.sessionStorage.getItem(RELOAD_KEY);
    if (stored) last = parseInt(stored, 10);
  } catch {
    /* stockage bloqué : on garde le flag module-level */
  }
  if (last && now - last < 10_000) {
    // Échec persistant (2ᵉ raté en <10s) : on ne re-reload pas et on LAISSE
    // l'erreur se propager (ErrorBoundary + Sentry) : c'est un vrai signal.
    return;
  }
  // On va recharger : l'erreur est gérée, inutile de la propager (évite le
  // flash d'ErrorBoundary pendant le reload + le bruit Sentry KENOMETRIC-2W).
  event.preventDefault();
  lastPreloadReloadAt = now;
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    /* stockage bloqué */
  }
  window.location.reload();
});

// Sentry - monitoring erreurs frontend (prod uniquement). Jamais depuis un site servi en local
// (Docker, essais automatisés) : la construction y est celle de production, mais les erreurs
// iraient dans le projet Sentry de production.
const hoteLocal = /^(localhost|127\.0\.0\.1|\[::1\])$|\.(localhost|test)$/.test(window.location.hostname);
if (import.meta.env.PROD && !hoteLocal) {
  Sentry.init({
    dsn: "https://91e21f405be2489bcd7b70fd120412cc@o4505968716677120.ingest.us.sentry.io/4510833352638464",
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.1,
    ignoreErrors: [
      /^Rejected$/,
      /Failed to register a ServiceWorker/,
      /Load failed/,
      /Failed to fetch/,
      /NetworkError/,
      /AbortError/,
      /NotAllowedError/,
      // Pont Java du navigateur in-app Facebook/Android détruit au unload
      // (script injecté iabjs://, cf KENOMETRIC-3K) : pas notre code.
      /Java object is gone/,
    ],
    denyUrls: [
      /google-analytics\.com/,
      /googletagmanager\.com/,
      // Scripts injectés par le navigateur in-app Facebook (perf logger, etc.)
      /^iabjs:\/\//,
    ],
    beforeSend(event) {
      // Ignore errors from Facebook/Instagram in-app browsers (injected scripts cause false positives)
      const ua = navigator.userAgent || "";
      if (/FBAN|FBAV|Instagram/i.test(ua)) {
        const msg = event.exception?.values?.[0]?.value || "";
        if (/Cannot read properties of undefined/.test(msg)) {
          return null;
        }
      }
      return event;
    },
  });
}

// Remove initial loader after React mounts
function hideInitialLoader() {
  const loader = document.getElementById("initial-loader");
  if (loader) {
    loader.classList.add("fade-out");
    setTimeout(() => loader.remove(), 200);
  }
}

// Retour d'un login OAuth (Google) : le callback serveur redirige en full-page
// vers `/home/?auth=google`. Comme l'OAuth ne passe pas par le login client-side,
// le flag `has_session` (qui autorise `useAuth` à vérifier la session) n'a jamais
// été posé → `/home/` rebondirait vers `/`. On le pose ICI, SYNCHRONE avant le
// mount React, pour que `AuthProvider` le lise dès son premier rendu. Le cookie
// de session (HttpOnly) reste la source de vérité : si invalide, la requête
// session renverra unauthenticated et l'event 401 nettoiera le flag.
try {
  if (new URLSearchParams(window.location.search).has("auth")) {
    window.localStorage.setItem("has_session", "true");
  }
} catch {
  /* localStorage bloqué (Safari « bloquer tous les cookies », webview) */
}

const container = document.getElementById("root") as HTMLDivElement;

// Pages prerendered self-contained (Header/Footer HTML statiques inclus, JS interactif inline) :
// on ne monte PAS React → LCP = FCP, plus de race React vs prerender DOM.
// Cas concret : /tirage/{date}/ teaser où SEO + performance priment sur l'interactivité SPA.
const isPrerenderedSelfMode = container?.querySelector("[data-prerendered-self]") !== null;
if (!isPrerenderedSelfMode) {
  createRoot(container).render(
    <HelmetProvider>
      <App />
    </HelmetProvider>
  );
}

// Hide loader after a short delay to ensure CSS is applied
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    hideInitialLoader();
  });
});
